/**
 * VideoCallService
 *
 * Domain owner of the 1:1 chat video-call data access. Bridges the data-layer
 * Cloud Function call to the domain by remapping its raw JSON into a
 * `LiveStreamAuthEntity`.
 *
 * Architecture:
 *   UseCase → VideoCallService → CloudFunctionsService
 *
 * Flutter source: lib/presentation/screens/chat/video_call_screen.dart:523-530
 *   (`_generateLiveStreamingToken` → `CloudFunctionsService.generateLiveStreamingToken(
 *    widget.groupChatId, _liveStreamingUserId, ClientRoleType.clientRoleBroadcaster.name)`)
 *   and lib/services/cloud_functions_service.dart:69-96.
 */

import type { Unsubscribe } from 'firebase/firestore';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { firestoreService } from '@/data/services/firestoreService';
import { RemoteDocumentChangeType } from '@/data/types/remoteDocumentChangeType';
import type { LiveStreamAuthEntity } from '@/domain/entities/liveStreamAuth/liveStreamAuthEntity';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import { LiveStreamAuthRemapper } from '@/domain/remappers/liveStreamAuth/liveStreamAuthRemapper';
import { ChatMessageRemapper } from '@/domain/remappers/chat/chatMessageRemapper';

export class VideoCallService {
  private static instance: VideoCallService;

  private constructor() {}

  static getInstance(): VideoCallService {
    if (!this.instance) {
      this.instance = new VideoCallService();
    }
    return this.instance;
  }

  /**
   * Fetch a fresh Agora live-stream token for the given channel and bridge the
   * raw Cloud Function JSON into a `LiveStreamAuthEntity`.
   *
   * The remapper is invoked here (the domain service) — never in the data layer
   * (per `data-layer.md`). `cloudFunctionsService.generateLiveStreamToken`
   * returns the inner `data` object as `JsonObject | null`; a `null` result
   * propagates as `null` (parity with `video_call_screen.dart:127`, where a null
   * auth — or a null `token` — aborts the call).
   *
   * @param channel - The Agora channel id (`chatUsersInfo.groupChatId`).
   * @param uid - The local streaming id (`_liveStreamingUserId`, 0 until joined).
   * @param role - The Agora client role wire string
   *   (`VideoCallRole.broadcaster` = `ClientRoleType.clientRoleBroadcaster.name`).
   */
  async generateLiveStreamToken(
    channel: string,
    uid: number,
    role: string,
  ): Promise<LiveStreamAuthEntity | null> {
    const json = await cloudFunctionsService.generateLiveStreamToken(channel, uid, role);
    if (json == null) return null;
    return LiveStreamAuthRemapper.fromDto(LiveStreamAuthRemapper.fromJson(json));
  }

  /**
   * Subscribe to the single `videoCall` chat message (identified by its
   * `videoCallId`) and surface it as a `ChatMessageEntity` through two distinct
   * callbacks that mirror two distinct Flutter responsibilities:
   *
   *   - `onModified` — the **lifecycle-reaction** path, gated on `modified` only
   *     (parity with Flutter's `change.type == DocumentChangeType.modified`
   *     guard, `video_call_screen.dart:361`). It drives the caller's
   *     audience-joined detection (`audienceStreamingId` set, `:364-369`) and
   *     the audience-side `isLive == false` teardown (`:375-381`). The
   *     `modified`-only gate is parity-critical and must stay here.
   *
   *   - `onLatest` — the **message-resolution** path, fired for EVERY change
   *     type (`added` / `modified` / `removed`). This is the web analog of
   *     Flutter's `_listItems`, which the chat screen feeds from a separate
   *     full-fidelity subscription handling all change types
   *     (`chat_screen.dart:507/582/608`). The three write-back use cases resolve
   *     their target message from this latest snapshot — without it, the
   *     create-time `added` change (Firestore delivers existing / just-created
   *     docs as `added`) is invisible to the `modified`-only path and every
   *     write-back no-ops on its `message != null` guard (business-parity
   *     finding 1). `onLatest` is the read source ONLY — it must NOT drive
   *     lifecycle reactions, or the `:361` parity would be broken.
   *
   * The raw Firestore `JsonObject` is remapped to a `ChatMessageEntity` here
   * (the domain service) via `ChatMessageRemapper.fromJson` → `fromDto` — the
   * consumer never sees raw JSON (per `data-layer.md`).
   *
   * The Flutter `latestLiveGiftInfo` branch (`video_call_screen.dart:383-400`)
   * is intentionally NOT ported — it is Phase 2 (task-prompt line 30).
   *
   * @param groupChatId - The chat node id (`chatUsersInfo.groupChatId`).
   * @param callMessageId - The call message's `videoCallId`.
   * @param onModified - Invoked with the remapped entity for each `modified`
   *   change only (lifecycle reactions, parity `:361`).
   * @param onLatest - Invoked with the remapped entity for EVERY change type,
   *   so the consumer can keep its message-resolution ref current (the web
   *   analog of `_listItems`). Optional — omit when only lifecycle reactions are
   *   needed.
   * @param onError - Optional error sink for the underlying snapshot listener.
   * @returns An `Unsubscribe`; the caller is responsible for cleanup.
   */
  subscribeToVideoCallMessage(
    groupChatId: string,
    callMessageId: string,
    onModified: (message: ChatMessageEntity) => void,
    onLatest?: (message: ChatMessageEntity) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return firestoreService.subscribeToVideoCallMessage(
      groupChatId,
      callMessageId,
      changes => {
        for (const change of changes) {
          const message = ChatMessageRemapper.fromDto(ChatMessageRemapper.fromJson(change.data));

          // Resolution source — fires for every change type so the consumer's
          // message ref tracks the latest doc state (the web analog of
          // `_listItems`, fed by all change types). NOT a lifecycle reaction.
          onLatest?.(message);

          // Lifecycle reactions — `modified` only, parity video_call_screen.dart:361.
          if (change.type !== RemoteDocumentChangeType.modified) continue;
          onModified(message);
        }
      },
      onError,
    );
  }
}

// Export singleton instance
export const videoCallService = VideoCallService.getInstance();
